import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const num = (v) => (v === "" || v == null ? null : Number(v));

const Row = ({ g, i, onChange, onRemove }) => (
  <div className="grid grid-cols-[1fr_1fr_2rem] items-center gap-2 border-b border-border pb-2 sm:grid-cols-[1fr_6rem_6rem_2rem] sm:border-0 sm:pb-0" data-testid={`guarantee-row-${i}`}>
    <Input value={g.label} onChange={(e) => onChange({ ...g, label: e.target.value })} placeholder="Garantie" className="col-span-3 sm:col-span-1" data-testid={`guarantee-label-${i}`} />
    <Input type="number" min="0" value={g.ceiling ?? ""} onChange={(e) => onChange({ ...g, ceiling: e.target.value })} placeholder="Plafond €" data-testid={`guarantee-ceiling-${i}`} />
    <Input type="number" min="0" value={g.deductible ?? ""} onChange={(e) => onChange({ ...g, deductible: e.target.value })} placeholder="Franchise €" data-testid={`guarantee-deductible-${i}`} />
    <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-destructive" aria-label="Retirer" data-testid={`guarantee-remove-${i}`}><X className="h-4 w-4" /></button>
  </div>
);

const Editor = ({ c, onDone }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const [list, setList] = useState(c.guarantees || []);
  const save = async () => {
    try {
      await api.put(`/households/${active.id}/insurance/${c.id}/guarantees`, list.filter((g) => g.label.trim()).map((g) => ({ label: g.label.trim(), ceiling: num(g.ceiling), deductible: num(g.deductible) })));
      qc.invalidateQueries({ queryKey: ["insurance"] });
      toast.success("Garanties enregistrées");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-2" data-testid={`guarantees-editor-${c.id}`}>
      <div className="grid grid-cols-[1fr_1fr_2rem] gap-2 text-[11px] font-semibold text-muted-foreground sm:grid-cols-[1fr_6rem_6rem_2rem]"><span className="hidden sm:block">Garantie</span><span>Plafond €</span><span>Franchise €</span></div>
      <div className="space-y-2">
        {list.map((g, i) => <Row key={i} g={g} i={i} onChange={(v) => setList(list.map((x, k) => (k === i ? v : x)))} onRemove={() => setList(list.filter((_, k) => k !== i))} />)}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setList([...list, { label: "", ceiling: null, deductible: null }])} data-testid={`guarantee-add-${c.id}`}><Plus className="mr-1 h-3.5 w-3.5" />Ajouter</Button>
        <Button size="sm" className="rounded-full" onClick={save} data-testid={`guarantees-save-${c.id}`}>Enregistrer</Button>
      </div>
    </div>
  );
};

export const GuaranteesSection = ({ c, canManage }) => {
  const { money } = useFormat();
  const [edit, setEdit] = useState(false);
  const gs = c.guarantees || [];
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Garanties ({gs.length})
        {canManage && <button className="normal-case text-primary underline" onClick={() => setEdit(!edit)} data-testid={`guarantees-edit-${c.id}`}>{edit ? "Fermer" : "Modifier"}</button>}</div>
      {edit ? <Editor c={c} onDone={() => setEdit(false)} /> : (
        <ul className="grid gap-1.5 sm:grid-cols-2" data-testid={`guarantees-${c.id}`}>
          {gs.map((g) => (
            <li key={g.label} className="flex items-baseline justify-between gap-2 rounded-lg bg-secondary/60 px-3 py-1.5 text-sm">
              <span className="min-w-0 truncate">{g.label}</span>
              <span className="num shrink-0 text-xs text-muted-foreground">{g.ceiling != null ? money(g.ceiling) : "—"}{g.deductible != null && ` · fr. ${money(g.deductible)}`}</span>
            </li>
          ))}
          {!gs.length && <li className="text-sm text-muted-foreground">Aucune garantie renseignée.</li>}
        </ul>
      )}
    </div>
  );
};
